import React from 'react'
import { CSSObject } from '@emotion/react'

import {
  AlignItems,
  Box,
  Color,
  colors,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Heading1,
  JustifyContent,
  Overflow,
  Text,
  TextAlign,
  VisuallyHidden,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { useMarketingPricingConfig } from '@library/api-pricing'

import { CWCDiscountPills } from '../CWCDiscountPills/CWCDiscountPills'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { CWCNewBadge } from '../CWCNewBadge/CWCNewBadge'
import { CWCHeroBackground } from './components/CWCHeroBackground/CWCHeroBackground'
import { DiscountBox } from '../AboveTheFoldSection/components/DiscountBanner/config'
import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../tracking'

export type CWCHeroProps = {
  discountBoxes: DiscountBox[]
  /** Sign-up destination the CTA links to (the anchor `href` — a real navigation target and no-JS fallback). */
  signupUrl: string
  /**
   * Shared signup handler (applies the promo code, then redirects). The CTA is a real `<a href>`, so
   * the click prevents the raw anchor navigation and dispatches this instead — matching the What's New
   * and First Box CTAs so the promo is applied consistently.
   */
  onCTAClick: () => void
}

// 4-value responsive arrays map to [base(<640), 640+, 768+ (tablet), 1024+ (desktop)].
// Tablet (768–1023) is a distinct band from desktop (≥1024) — a 3-value array would
// lump every width ≥768 onto the desktop value. See AboveTheFoldSection for the convention.

// Headline — ExtraBold, no letter-spacing. Brand sizes: 40/44 mobile, 56/64 tablet (≥768),
// 72/80 desktop (≥1024). textAlign is set per-breakpoint at the call site. Sizes go via
// responsiveStyle (media-wrapped) because a plain fontSize loses to Citrus's own @media default.
const Headline = withCitrusTheme(Heading1, (theme) => [
  { fontWeight: 800, marginTop: '10px' } as CSSObject,
  theme.responsiveStyle(['40px', '40px', '56px', '72px'], ['fontSize']) as CSSObject,
  theme.responsiveStyle(['44px', '44px', '64px', '80px'], ['lineHeight']) as CSSObject,
])

// Responsive line-break helpers for the headline. Rendered as <span> (valid phrasing content
// inside an <h1>) toggled block/none per breakpoint — a display:block span forces the wrap. A
// Box here would emit a <div>, which is invalid inside a heading and risks SSR hydration splits.
// The 640–767 band is still mobile (tablet only starts at 768), so it keeps the mobile three-line
// wrap; the tablet two-line wrap is confined to index 2 (768–1023). The arrays must switch at 768,
// not 640, to stay consistent with the mobile headline font/layout in that band.
const BreakMobileDesktop = withCitrusTheme('span', (theme) =>
  theme.responsiveStyle(['block', 'block', 'none', 'block'], ['display']),
)
const BreakTablet = withCitrusTheme('span', (theme) =>
  theme.responsiveStyle(['none', 'none', 'block', 'none'], ['display']),
)

// Content column. Desktop (Figma 183:82): 128px left padding and an 80px gap between the heading
// group and the offer group; 80px isn't expressible as a SpaceScale marginer so it's set here.
// Mobile/tablet keep 24px side padding and no gap (the column uses space-between instead).
const ContentWrapper = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(['0px', '0px', '0px', '80px'], ['gap']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '24px', '128px'], ['paddingLeft']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '24px', '24px'], ['paddingRight']) as CSSObject,
])

// Eyebrow "Meet the NEW Gousto" — Axiforma Bold. Brand sizes: 17/17.85 mobile, 24/32 tablet (≥768),
// 28/36 desktop (≥1024).
const EyebrowText = withCitrusTheme(Text, (theme) => [
  { fontWeight: 700 } as CSSObject,
  theme.responsiveStyle(['17px', '17px', '24px', '28px'], ['fontSize']) as CSSObject,
  theme.responsiveStyle(['17.85px', '17.85px', '32px', '36px'], ['lineHeight']) as CSSObject,
])

// Wraps the shared CWCLinkButton to reproduce the spacing/width the previous bespoke CTALink carried
// itself: a top margin between the discount pills and the CTA, and the 100%→345px responsive width.
// The button fills this wrapper (its own width is 100% up to the 345px cap), so framing is preserved.
const CTAWrapper = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(['12px', '18px', '18px', '18px'], ['marginTop']) as CSSObject,
  theme.responsiveStyle(['100%', '345px', '345px', '345px'], ['width']) as CSSObject,
])

const BenefitsContainer = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(['10px', '12px', '12px'], ['marginTop']) as CSSObject),
}))

const PriceText = withCitrusTheme(Text, (theme) => ({
  color: theme.color(Color.White),
}))

// Dark fallback behind the background image. On tablet the image is shifted down to open the
// headline↔box gap, so it no longer covers the very top of the hero; this colour matches the
// image's top edge (≈rgb(32,19,11)) so the uncovered strip is seamless under the dark gradient.
// Mobile/desktop images cover the hero fully, so it's never visible there.
const HeroRoot = withCitrusTheme(Box, () => ({
  backgroundColor: '#20130b',
}))

export function CWCHero({ discountBoxes, signupUrl, onCTAClick }: CWCHeroProps) {
  const defaultPricing = { pricePerPortion: '3.20', discountedPricePerPortion: '1.60' }
  const pricing = useMarketingPricingConfig()
  const pricePerPortion = pricing?.pricePerPortion ?? defaultPricing.pricePerPortion
  const discountedPricePerPortion =
    pricing?.discountedPricePerPortion ?? defaultPricing.discountedPricePerPortion

  const handleSignupClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // The CTA is a real <a href>, so prevent the raw anchor navigation and dispatch the shared
    // signup handler instead (applies the promo, then redirects) — matching the What's New CTA.
    event.preventDefault()
    onCTAClick()
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, LandingPageContentSections.HERO)
  }

  return (
    <HeroRoot
      data-testid="hero"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      // Desktop is now a definite-height hero (like mobile/tablet) so the content column can be
      // vertically centred (Figma centres it); the px heights are tuning knobs — real-page check.
      height={['812px', '812px', '1000px', '1000px']}
      minHeight={['812px', '812px', '1000px', '1000px']}
    >
      <CWCHeroBackground />
      <ContentWrapper
        position={DisplayPosition.Relative}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={[
          JustifyContent.SpaceBetween,
          JustifyContent.SpaceBetween,
          JustifyContent.SpaceBetween,
          JustifyContent.Center,
        ]}
        alignItems={[AlignItems.Center, AlignItems.Center, AlignItems.Center, AlignItems.FlexStart]}
        height={['100%', '100%', '100%', '100%']}
        // Tablet (index 2) top padding is 64px per Figma 183:136; paddingBottom keeps
        // the tablet bottom at 40px since paddingV would otherwise apply the 64px symmetrically.
        // Horizontal padding lives on ContentWrapper (paddingLeft/Right) now.
        paddingV={[8, 10, 16, 14]}
        paddingBottom={[8, 10, 10, 14]}
      >
        <Box
          width="100%"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={[
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.FlexStart,
          ]}
        >
          <Box display={Display.Flex} alignItems={AlignItems.Center} gap={0.75}>
            {/* The sticker itself is decorative (aria-hidden) — "New" is kept as real,
                visually-hidden text so assistive tech still hears "Meet the New Gousto",
                matching what this eyebrow said before the sticker replaced the literal word. */}
            <EyebrowText color={Color.White}>Meet the </EyebrowText>
            <VisuallyHidden>New </VisuallyHidden>
            <CWCNewBadge width={['62px', null, '67px']} height={['44px', null, '48px']} />
            <EyebrowText color={Color.White}>Gousto</EyebrowText>
          </Box>
          <Headline
            color={Color.White}
            fontFamily={FontFamily.ExtraBold}
            textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
          >
            {/* Responsive line breaks: mobile/desktop wrap "The recipe box / that works / around
                you", tablet wraps "The recipe box that / works around you" (design 183:141). Spans
                toggle block (break) or none per breakpoint — valid inside an <h1>. */}
            {'The recipe box'}
            <BreakMobileDesktop />
            {' that '}
            <BreakTablet />
            {'works'}
            <BreakMobileDesktop />{' '}
            <span style={{ color: colors[Color.Primary_600] }}>around you</span>
          </Headline>
        </Box>
        <Box
          width="100%"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={[
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.FlexStart,
          ]}
        >
          <CWCDiscountPills
            discountBoxes={discountBoxes}
            price={{ pricePerPortion, discountedPricePerPortion }}
          />
          <CTAWrapper>
            {/* fontSizeOverride restores the Hero CTA's Figma-verified 18px label — CWCLinkButton's
                primary variant defaults to 16px. Line-height isn't overridden: the label is a single
                line, flex-centred in the 48px-min-height button, so the old 28px line-height had no
                visual effect here. */}
            <CWCLinkButton href={signupUrl} onClick={handleSignupClick} fontSizeOverride="18px">
              Get started
            </CWCLinkButton>
          </CTAWrapper>
          <BenefitsContainer
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            width={['100%', '400px', '400px']}
          >
            <PriceText
              size={2}
              textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
            >
              <span style={{ fontWeight: FontWeight.Bold }}>Free next-day delivery</span> on your
              1st box<span style={{ fontSize: '9px', verticalAlign: 'super' }}>†</span>
            </PriceText>
            <PriceText
              size={2}
              textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
            >
              <span style={{ fontWeight: FontWeight.Bold }}>Skip or cancel</span> for free anytime
            </PriceText>
          </BenefitsContainer>
        </Box>
      </ContentWrapper>
    </HeroRoot>
  )
}
